/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { useParams, useNavigate } from 'react-router-dom'
import {
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaBuilding,
  FaArrowLeft,
  FaRupeeSign,
  FaUser,
  FaUsers,
  FaCheckCircle,
  FaMoneyBillWave,
  FaIdCard,
  FaSpinner,
  FaCreditCard,
  FaShieldAlt,
  FaExclamationCircle,
  FaTag
} from 'react-icons/fa'
import ReCAPTCHA from 'react-google-recaptcha'
import Footer from '../components/Footer'

// Razorpay script loader utility function
const loadRazorpayScript = () => {
  return new Promise(resolve => {
    const script = document.createElement('script')
    script.src = 'https://checkout.razorpay.com/v1/checkout.js'
    script.onload = () => {
      resolve(true)
    }
    script.onerror = () => {
      resolve(false)
    }
    document.body.appendChild(script)
  })
}

const EventFormPage = () => {
  const { eventId } = useParams()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [event, setEvent] = useState(null)
  const [error, setError] = useState(null)
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    city: '',
    state: '',
    joinAs: '',
    companyName: '',
    challenge: [],
    businessDescription: '',
    otherInformation: '',
    file: null,
    photo: null,
    registrationType: 'nonMember',
    memberId: '',
    memberVerified: false,
    memberDetails: null
  })
  const [formSubmitted, setFormSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [captchaToken, setCaptchaToken] = useState(null)
  const [verifyingMember, setVerifyingMember] = useState(false)
  const [paymentLoading, setPaymentLoading] = useState(false)
  const [registrationStep, setRegistrationStep] = useState(1)

  // Dynamic options from event
  const [joinAsOptions, setJoinAsOptions] = useState([])
  const [challengeOptions, setChallengeOptions] = useState([])

  // Member data state
  const [allMembers, setAllMembers] = useState([])
  const [loadingMembers, setLoadingMembers] = useState(false)
  const [membersLoaded, setMembersLoaded] = useState(false)

  // API URLs
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL
  const API_EVENTS = `${API_BASE_URL}/UIevents`
  const API_MEDIA = `${API_BASE_URL}/UImedia`
  const API_REGISTRATIONS = `${API_BASE_URL}/UI/event-registrations`
  const API_PAID_MEMBERS = `${API_BASE_URL}/paid-members`
  const API_OUR_MEMBERS = `${API_BASE_URL}/UIourMembers`

  // Fetch ALL members data
  useEffect(() => {
    const fetchAllMembers = async () => {
      try {
        setLoadingMembers(true)

        // Fetch paid members
        const paidMembersResponse = await axios.get(API_PAID_MEMBERS)
        const paidMembersData = paidMembersResponse.data
        const paidMembers =
          paidMembersData.success && Array.isArray(paidMembersData.items)
            ? paidMembersData.items
            : []

        // Fetch our members
        const ourMembersResponse = await axios.get(API_OUR_MEMBERS)
        const ourMembersData = ourMembersResponse.data
        const ourMembers = Array.isArray(ourMembersData) ? ourMembersData : []

        // Process and combine all members
        const processedPaidMembers = paidMembers
          .map(member => ({
            id: member.id || member._id,
            memberId: member.memberId,
            name: member.formData?.fullName || member.name || 'N/A',
            email: member.formData?.email || member.email,
            phone:
              member.formData?.contactNumber ||
              member.contactNumber ||
              member.phone,
            memberType: 'paid',
            paymentStatus: member.paymentStatus,
            verificationStatus: member.verificationStatus,
            planTitle: member.planTitle,
            rawData: member
          }))
          .filter(member => member.memberId)

        const processedOurMembers = ourMembers
          .map(member => ({
            id: member.id || member._id,
            memberId: member.memberId,
            name: member.name,
            email: member.email,
            phone: member.contactNumber || member.phone,
            memberType: 'regular',
            memberTypeCode: member.memberTypeCode,
            paymentStatus: member.paymentStatus,
            isActive: member.isActive,
            rawData: member
          }))
          .filter(member => member.memberId)

        const combinedMembers = [
          ...processedPaidMembers,
          ...processedOurMembers
        ]

        setAllMembers(combinedMembers)
        setMembersLoaded(true)
      } catch (error) {
        console.error('Error fetching members data:', error)
      } finally {
        setLoadingMembers(false)
      }
    }

    fetchAllMembers()
  }, [API_PAID_MEMBERS, API_OUR_MEMBERS])

  // Fetch event details
  useEffect(() => {
    const fetchEventDetails = async () => {
      try {
        setLoading(true)
        setError(null)

        const response = await axios.get(`${API_EVENTS}/${eventId}`)
        const eventData = response.data

        let imageUrl =
          eventData.image ||
          'https://images.unsplash.com/photo-1511795409834-ef04bbd61622?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80'

        if (eventData.image && eventData.image.id) {
          try {
            const imgRes = await axios.get(
              `${API_MEDIA}/${eventData.image.id}/download`,
              {
                responseType: 'blob'
              }
            )
            imageUrl = URL.createObjectURL(imgRes.data)
          } catch (imgErr) {
            console.error('Error loading image:', imgErr)
          }
        }

        // ✅ DYNAMIC: Extract joinAs and challenges from event data
        const eventJoinAs = eventData.joinAs || []
        const eventChallenges = eventData.challenges || []

        // Update state with dynamic options
        setJoinAsOptions(eventJoinAs)
        setChallengeOptions(eventChallenges)

        const formattedEvent = {
          id: eventData.id || eventData._id,
          title: eventData.title || 'Untitled Event',
          date: eventData.date
            ? formatDate(eventData.date)
            : 'Date not specified',
          location: eventData.location || 'Location not specified',
          description: eventData.description || '',
          fullDescription: eventData.fullDescription || '',
          otherDescription: eventData.otherDescription || '',
          image: imageUrl,
          category: eventData.category || 'upcoming',
          status: eventData.status || 'upcoming',
          time: eventData.time || '',
          organizer: eventData.organizer || 'MASA Forum',
          eventType: eventData.eventType || 'free',
          memberAmount: eventData.memberAmount || 0,
          nonMemberAmount: eventData.nonMemberAmount || 0,
          joinAs: eventJoinAs, // Store for reference
          challenges: eventChallenges, // Store for reference
          showOtherInfo: eventData.showOtherInfo ?? true,
          showPhotoUpload: eventData.showPhotoUpload ?? true,
          showDocumentUpload: eventData.showDocumentUpload ?? true
        }

        setEvent(formattedEvent)

        if (formattedEvent.eventType === 'paid') {
          setFormData(prev => ({
            ...prev,
            registrationType: 'nonMember'
          }))
        }
      } catch (err) {
        console.error('Error fetching event details:', err)
        setError('Failed to load event details. Please try again.')
      } finally {
        setLoading(false)
      }
    }

    if (eventId) {
      fetchEventDetails()
    }
  }, [eventId, API_EVENTS, API_MEDIA])

  // Format date
  const formatDate = dateString => {
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      })
    } catch (e) {
      return dateString
    }
  }
  const isSamePrice =
    event && Number(event.memberAmount) === Number(event.nonMemberAmount)
  // Calculate amount based on registration type
  const calculateAmount = () => {
    if (!event || event.eventType === 'free') return 0

    if (formData.registrationType === 'member' && formData.memberVerified) {
      return event.memberAmount
    } else {
      return event.nonMemberAmount
    }
  }

  // Handle back button
  const handleBack = () => {
    navigate('/events')
  }

  // Handle form input changes
  const handleInputChange = e => {
    const { name, value, type, files, checked } = e.target

    // ✅ MULTI SELECT CHECKBOX (FINAL FIX)
    if (type === 'checkbox' && name === 'challenge') {
      setFormData(prev => ({
        ...prev,
        challenge: checked
          ? [...prev.challenge, value]
          : prev.challenge.filter(item => item !== value)
      }))
      return // 🔥 VERY IMPORTANT
    }

    // ✅ FILE INPUT
    if (type === 'file') {
      setFormData(prev => ({
        ...prev,
        [name]: files[0]
      }))
      return
    }

    // ✅ NORMAL INPUTS (text, radio, email etc.)
    setFormData(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'memberId' && {
        memberVerified: false,
        memberDetails: null
      })
    }))
  }

  // Handle registration type change
  const handleRegistrationTypeChange = type => {
    setFormData({
      ...formData,
      registrationType: type,
      memberId: '',
      memberVerified: false,
      memberDetails: null
    })
  }

  // Verify member ID - LOCAL CHECK
  const verifyMemberId = async () => {
    if (!formData.memberId.trim()) {
      setError('Please enter Member ID')
      return
    }

    setVerifyingMember(true)
    setError(null)

    try {
      const searchId = formData.memberId.trim().toUpperCase()

      // First try local check
      if (membersLoaded && allMembers.length > 0) {
        const foundMember = allMembers.find(member => {
          const memberIdUpper = member.memberId
            ? member.memberId.toUpperCase()
            : ''
          const idUpper = member.id ? member.id.toString().toUpperCase() : ''
          const emailUpper = member.email ? member.email.toUpperCase() : ''

          return (
            memberIdUpper === searchId ||
            idUpper === searchId ||
            emailUpper === searchId
          )
        })

        if (foundMember) {
          setFormData(prev => ({
            ...prev,
            memberVerified: true,
            memberDetails: foundMember
          }))
          setVerifyingMember(false)
          return
        }
      }

      // Fallback to API check
      let response

      try {
        response = await axios.get(`${API_PAID_MEMBERS}/${searchId}`)

        if (response.data && response.data.success) {
          const memberData = response.data.items?.[0] || response.data
          setFormData(prev => ({
            ...prev,
            memberVerified: true,
            memberDetails: {
              id: memberData.id,
              memberId: memberData.memberId,
              name: memberData.formData?.fullName || memberData.name,
              email: memberData.formData?.email || memberData.email,
              phone:
                memberData.formData?.contactNumber || memberData.contactNumber,
              memberType: 'paid',
              rawData: memberData
            }
          }))
          setVerifyingMember(false)
          return
        }
      } catch (paidError) {
        // console.log("Not found in paid members");
      }

      // Try our members API
      try {
        response = await axios.get(`${API_OUR_MEMBERS}/${searchId}`)

        if (response.data) {
          setFormData(prev => ({
            ...prev,
            memberVerified: true,
            memberDetails: {
              id: response.data.id,
              memberId: response.data.memberId,
              name: response.data.name,
              email: response.data.email,
              phone: response.data.contactNumber,
              memberType: 'regular',
              rawData: response.data
            }
          }))
          setVerifyingMember(false)
          return
        }
      } catch (ourError) {
        // console.log("Not found in our members either");
      }

      setError('Member ID not found. Please check and try again.')
    } catch (error) {
      console.error('Error verifying member:', error)
      setError('Member ID not found. Please check and try again.')
    } finally {
      setVerifyingMember(false)
    }
  }

  // Optimized member ID verification on input change
  const handleMemberIdBlur = () => {
    if (
      formData.memberId.trim() &&
      formData.registrationType === 'member' &&
      !formData.memberVerified
    ) {
      if (membersLoaded && allMembers.length > 0) {
        const searchId = formData.memberId.trim().toUpperCase()

        const foundMember = allMembers.find(member => {
          const memberIdUpper = member.memberId
            ? member.memberId.toUpperCase()
            : ''
          const idUpper = member.id ? member.id.toString().toUpperCase() : ''
          const emailUpper = member.email ? member.email.toUpperCase() : ''

          return (
            memberIdUpper === searchId ||
            idUpper === searchId ||
            emailUpper === searchId
          )
        })

        if (foundMember) {
          setFormData(prev => ({
            ...prev,
            memberVerified: true,
            memberDetails: foundMember
          }))
          setError(null)
        }
      }
    }
  }

  const handleCaptchaChange = token => {
    setCaptchaToken(token)
  }

  // Upload file and get media ID
  const uploadFile = async file => {
    if (!file) return null

    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('name', file.name)
      formData.append('type', file.type)

      const response = await axios.post(`${API_MEDIA}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })

      return response.data.id || response.data._id
    } catch (error) {
      console.error('Error uploading file:', error)
      return null
    }
  }

  // Process Razorpay payment
  const processPayment = async () => {
    try {
      setPaymentLoading(true)

      // Load Razorpay script
      const razorpayLoaded = await loadRazorpayScript()
      if (!razorpayLoaded) {
        throw new Error(
          'Failed to load payment gateway. Please refresh and try again.'
        )
      }

      const amount = calculateAmount() // Amount in rupees

      // console.log("Creating order for amount:", amount);

      // Create order on your backend
      const orderResponse = await axios.post(
        `${API_BASE_URL}/UI/create-razorpay-order`,
        {
          amount: amount, // Send amount in rupees
          currency: 'INR',
          eventId: eventId,
          memberId:
            formData.registrationType === 'member' ? formData.memberId : null,
          registrationType: formData.registrationType,
          name: formData.name,
          email: formData.email,
          phone: formData.phone
        }
      )

      const orderData = orderResponse.data

      if (!orderData.success) {
        throw new Error(orderData.message || 'Failed to create order')
      }

      // console.log("Order created:", orderData);

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: orderData.order.amount, // Use amount from backend (already in paise)
        currency: orderData.order.currency,
        name: event.title,
        description: `Event Registration for ${event.title}`,
        order_id: orderData.order.id,
        handler: async function (response) {
          try {
            // console.log("Payment successful:", response);

            // ✅ CORRECT: Use correct field names
            const verifyResponse = await axios.post(
              `${API_BASE_URL}/UI/verify-razorpay-payment`,
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                eventId: eventId,
                registrationData: {
                  ...formData,
                  registrationType: formData.registrationType,
                  memberId:
                    formData.registrationType === 'member'
                      ? formData.memberId
                      : null,
                  amountPaid: calculateAmount(),
                  captchaToken: captchaToken
                }
              }
            )

            // console.log("Verification response:", verifyResponse.data);

            if (verifyResponse.data.success) {
              // Payment successful
              setRegistrationStep(3)
              setFormSubmitted(true)
            } else {
              setError('Payment verification failed. Please try again.')
              setPaymentLoading(false)
            }
          } catch (error) {
            console.error(
              'Payment verification error:',
              error.response?.data || error
            )
            setError(
              error.response?.data?.message ||
                'Payment verification failed. Please try again.'
            )
            setPaymentLoading(false)
          }
        },
        prefill: {
          name: formData.name,
          email: formData.email,
          contact: formData.phone
        },
        theme: {
          color: '#4F46E5'
        },
        modal: {
          ondismiss: function () {
            setPaymentLoading(false)
          }
        }
      }

      const razorpay = new window.Razorpay(options)
      razorpay.open()
    } catch (error) {
      console.error('Payment error:', error.response?.data || error)
      setError(
        error.response?.data?.message ||
          'Payment processing failed. Please try again.'
      )
      setPaymentLoading(false)
    }
  }

  // Submit registration for FREE events
  const submitRegistration = async (paymentId = null, invitedId = null) => {
    try {
      setSubmitting(true)

      // 1. Upload file if exists
      const fileId = await uploadFile(formData.file)
      const photoId = await uploadFile(formData.photo)

      // 2. Prepare registration data
      const registrationData = {
        eventId: eventId,
        eventTitle: event.title,
        eventDate: event.date,
        eventLocation: event.location,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        city: formData.city,
        state: formData.state,
        joinAs: formData.joinAs,
        companyName: formData.companyName,
        challenge: Array.isArray(formData.challenge)
          ? formData.challenge
          : [formData.challenge],
        businessDescription: formData.businessDescription,
        otherInformation: formData.otherInformation,
        fileId: fileId,
        photoId: photoId,
        captchaToken: captchaToken,
        registrationType: formData.registrationType,
        memberId:
          formData.registrationType === 'member' ? formData.memberId : null,
        amountPaid: calculateAmount(),
        paymentId: paymentId,
        paymentStatus: paymentId ? 'paid' : 'free',
        eventType: event.eventType,
        invitedId: invitedId // Include invitedId for validation
      }

      // 3. Send registration data to backend
      const response = await axios.post(API_REGISTRATIONS, registrationData)
      // console.log("Registration successful:", response.data);

      // 4. Show success message
      setRegistrationStep(3)
      setFormSubmitted(true)
    } catch (error) {
      console.error('Error submitting form:', error.response?.data || error)
      setError(
        error.response?.data?.message ||
          'Failed to submit registration. Please try again.'
      )
      setSubmitting(false)
    }
  }

  // Handle form submission
  const handleSubmit = async e => {
    e.preventDefault()
    setSubmitting(true)
    setError(null)

    // console.log("Form submitted - Event type:", event?.eventType); // Debug

    if (!captchaToken) {
      setError('Please verify that you are not a robot.')
      setSubmitting(false)
      return
    }

    // Validate required fields - Remove joinAs from required fields since it's now optional
    const requiredFields = ['name', 'email', 'phone', 'city', 'state']
    const missingFields = requiredFields.filter(
      field => !formData[field].trim()
    )

    if (missingFields.length > 0) {
      setError(`Please fill all required fields: ${missingFields.join(', ')}`)
      setSubmitting(false)
      return
    }

    // console.log("All validations passed. Submitting..."); // Debug

    // ✅ FIXED LOGIC:
    if (event.eventType === 'paid') {
      // console.log("Paid event - Going to payment step");

      if (formData.registrationType === 'member' && !formData.memberVerified) {
        setError('Please verify your Member ID first.')
        setSubmitting(false)
        return
      }

      setRegistrationStep(2)
      setSubmitting(false)
    } else {
      // console.log("Free event - Direct submission");

      await submitRegistration()
    }
  }

  // Handle payment step
  const handlePayment = async () => {
    if (event.eventType === 'paid') {
      await processPayment()
    } else {
      await submitRegistration()
    }
  }

  // Go back to previous step
  const handleGoBack = () => {
    if (registrationStep === 2) {
      setRegistrationStep(1)
    }
  }

  if (loading) {
    return (
      <div className='min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 text-gray-100 flex items-center justify-center'>
        <div className='text-center'>
          <div className='inline-block animate-spin rounded-full h-16 w-16 border-4 border-teal-500 border-t-transparent'></div>
          <p className='mt-6 text-gray-400 text-lg'>Loading event details...</p>
        </div>
      </div>
    )
  }

  if (error && !event) {
    return (
      <div className='min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 text-gray-100 flex items-center justify-center'>
        <div className='text-center'>
          <div className='text-red-500 text-4xl mb-4'>⚠️</div>
          <h2 className='text-2xl font-bold mb-2'>Error Loading Event</h2>
          <p className='text-gray-400 mb-6'>{error}</p>
          <button
            onClick={handleBack}
            className='px-6 py-3 bg-teal-600 hover:bg-teal-700 rounded-lg font-medium transition-colors'
          >
            Back to Events
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className='min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 text-gray-100'>
      {/* Header with Back Button */}
      <div className='container mx-auto px-4 pt-6'>
        <button
          onClick={handleBack}
          className='flex items-center gap-2 text-gray-300 hover:text-white transition-colors mb-6'
        >
          <FaArrowLeft />
          <span>Back to Events</span>
        </button>
      </div>

      {/* Main Content */}
      <div className='container mx-auto px-4 md:px-6 py-8'>
        <div className='max-w-7xl mx-auto'>
          {/* Full Width Image */}
          <div className='relative overflow-hidden rounded-2xl mb-8'>
            {/* Image container with compulsory full display for 1200x800 */}
            <div className='relative w-full h-64 md:h-80 lg:h-96 overflow-hidden flex items-center justify-center  '>
              <img
                src={event.image}
                alt={event.title}
                className='w-full h-full object-contain'
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain' // Full image visible without crop
                }}
                onError={e => {
                  e.target.src =
                    'https://images.unsplash.com/photo-1511795409834-ef04bbd61622?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80'
                }}
              />
            </div>

            {/* Content neeche alag se */}
            <div className='mt-6 p-6 md:p-8'>
              <h1 className='text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4'>
                {event.title}
              </h1>
              <div className='flex flex-wrap gap-4 items-center'>
                {event.eventType === 'paid' && (
                  <span className='px-4 py-2 rounded-full text-sm font-bold bg-purple-500 text-white'>
                    💰 PAID EVENT
                  </span>
                )}
              </div>
            </div>
          </div>
          {/* Registration Progress Steps */}
          <div className='mb-8'>
            <div className='flex justify-between items-center'>
              {[1, 2, 3].map(step => (
                <div key={step} className='flex items-center'>
                  <div
                    className={`flex items-center justify-center w-10 h-10 rounded-full ${
                      registrationStep >= step
                        ? 'bg-teal-500 text-white'
                        : 'bg-gray-700 text-gray-400'
                    }`}
                  >
                    {step}
                  </div>
                  {step < 3 && (
                    <div
                      className={`h-1 w-20 md:w-32 ${
                        registrationStep > step ? 'bg-teal-500' : 'bg-gray-700'
                      }`}
                    ></div>
                  )}
                </div>
              ))}
            </div>
            <div className='flex justify-between mt-2 text-sm'>
              <span
                className={`${
                  registrationStep >= 1 ? 'text-teal-400' : 'text-gray-500'
                }`}
              >
                Registration Details
              </span>
              <span
                className={`${
                  registrationStep >= 2 ? 'text-teal-400' : 'text-gray-500'
                }`}
              >
                {event.eventType === 'paid' ? 'Payment' : 'Review'}
              </span>
              <span
                className={`${
                  registrationStep >= 3 ? 'text-teal-400' : 'text-gray-500'
                }`}
              >
                Confirmation
              </span>
            </div>
          </div>

          <div className='grid grid-cols-1 lg:grid-cols-3 gap-8'>
            {/* Event Info Section */}
            <div className='lg:col-span-1'>
              <div className='bg-gradient-to-b from-gray-800/60 to-gray-900/40 rounded-2xl p-6 border border-gray-700 backdrop-blur-sm sticky top-6'>
                <h2 className='text-xl font-bold text-white mb-6 pb-4 border-b border-gray-700'>
                  Event Information
                </h2>

                <div className='space-y-5'>
                  <div className='flex items-start gap-4 p-4 bg-gray-800/40 rounded-xl'>
                    <div className='p-3 bg-teal-900/30 rounded-lg'>
                      <FaCalendarAlt className='text-teal-400 text-xl' />
                    </div>
                    <div>
                      <p className='text-sm text-gray-400 mb-1'>Date & Time</p>
                      <p className='text-white font-medium'>{event.date}</p>
                      <p className='text-teal-300'>{event.time}</p>
                    </div>
                  </div>

                  <div className='flex items-start gap-4 p-4 bg-gray-800/40 rounded-xl'>
                    <div className='p-3 bg-blue-900/30 rounded-lg'>
                      <FaMapMarkerAlt className='text-blue-400 text-xl' />
                    </div>
                    <div>
                      <p className='text-sm text-gray-400 mb-1'>Location</p>
                      <p className='text-white font-medium'>{event.location}</p>
                    </div>
                  </div>

                  <div className='flex items-start gap-4 p-4 bg-gray-800/40 rounded-xl'>
                    <div className='p-3 bg-yellow-900/30 rounded-lg'>
                      <FaBuilding className='text-yellow-400 text-xl' />
                    </div>
                    <div>
                      <p className='text-sm text-gray-400 mb-1'>Organizer</p>
                      <p className='text-white font-medium'>
                        {event.organizer}
                      </p>
                    </div>
                  </div>

                  {event.eventType === 'paid' && (
                    <div className='mt-4 p-4 bg-gradient-to-r from-purple-900/30 to-pink-900/30 rounded-xl border border-purple-700/50'>
                      <h3 className='text-lg font-bold text-white mb-3 flex items-center gap-2'>
                        <FaMoneyBillWave className='text-purple-400' />
                        Event Pricing
                      </h3>
                      <div className='space-y-2'>
                        <div className='flex justify-between items-center p-2 bg-purple-900/20 rounded-lg'>
                          <div className='flex items-center gap-2'>
                            <FaUser className='text-purple-300' />
                            <span className='text-gray-300'>Member Price</span>
                          </div>
                          <div className='flex items-center'>
                            <FaRupeeSign className='text-purple-300' />
                            <span className='text-white font-bold'>
                              {event.memberAmount}
                            </span>
                          </div>
                        </div>
                        <div className='flex justify-between items-center p-2 bg-purple-900/20 rounded-lg'>
                          <div className='flex items-center gap-2'>
                            <FaUsers className='text-purple-300' />
                            <span className='text-gray-300'>
                              Non-Member Price
                            </span>
                          </div>
                          <div className='flex items-center'>
                            <FaRupeeSign className='text-purple-300' />
                            <span className='text-white font-bold'>
                              {event.nonMemberAmount}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right Column with Form Steps */}
            <div className='lg:col-span-2 space-y-8'>
              {/* Step 1: Registration Form */}
              {registrationStep === 1 && (
                <div className='bg-gradient-to-r from-gray-800/50 to-gray-900/50 rounded-2xl p-6 md:p-8 border border-gray-700 backdrop-blur-sm'>
                  <div className='flex items-center justify-between mb-6 pb-4 border-b border-gray-700'>
                    <h2 className='text-2xl font-bold text-white'>
                      Registration Details
                    </h2>
                    <div className='text-sm text-gray-400'>
                      Step 1 of {event.eventType === 'paid' ? '3' : '2'}
                    </div>
                  </div>

                  {error && (
                    <div className='mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg'>
                      <p className='text-red-400'>{error}</p>
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className='space-y-6'>
                    {/* Registration Type for Paid Events */}
                    {event.eventType === 'paid' && (
                      <div className='p-4 bg-gradient-to-r from-purple-900/20 to-pink-900/20 rounded-xl border border-purple-700/30'>
                        <label className='block text-sm font-medium text-gray-300 mb-3'>
                          Select Registration Type *
                        </label>
                        <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
                          <label
                            className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer ${
                              formData.registrationType === 'member'
                                ? 'bg-purple-900/40 border-purple-500'
                                : 'bg-gray-800/40 border-gray-700'
                            }`}
                          >
                            <input
                              type='radio'
                              name='registrationType'
                              value='member'
                              checked={formData.registrationType === 'member'}
                              onChange={() =>
                                handleRegistrationTypeChange('member')
                              }
                              className='w-5 h-5 text-purple-500'
                            />
                            <div>
                              <div className='flex items-center gap-2 mb-1'>
                                <FaUser className='text-purple-400' />
                                <span className='text-white font-bold'>
                                  Member Registration
                                </span>
                              </div>
                              {!isSamePrice && (
                                <p className='text-sm text-gray-400'>
                                  ₹{event.memberAmount}
                                </p>
                              )}
                            </div>
                          </label>
                          <label
                            className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer ${
                              formData.registrationType === 'nonMember'
                                ? 'bg-purple-900/40 border-purple-500'
                                : 'bg-gray-800/40 border-gray-700'
                            }`}
                          >
                            <input
                              type='radio'
                              name='registrationType'
                              value='nonMember'
                              checked={
                                formData.registrationType === 'nonMember'
                              }
                              onChange={() =>
                                handleRegistrationTypeChange('nonMember')
                              }
                              className='w-5 h-5 text-purple-500'
                            />
                            <div>
                              <div className='flex items-center gap-2 mb-1'>
                                <FaUsers className='text-purple-400' />
                                <span className='text-white font-bold'>
                                  Non-Member Registration
                                </span>
                              </div>
                              {!isSamePrice && (
                                <p className='text-sm text-gray-400'>
                                  ₹{event.nonMemberAmount}
                                </p>
                              )}
                            </div>
                          </label>
                        </div>
                      </div>
                    )}
                    {event.eventType === 'paid' && isSamePrice && (
                      <div className='mt-4 p-4 rounded-xl bg-purple-900/30 border border-purple-700/40'>
                        <div className='flex justify-between items-center'>
                          <span className='text-gray-300 font-medium'>
                            Registration Fee
                          </span>

                          <div className='flex items-center text-2xl font-bold text-white'>
                            <FaRupeeSign className='mr-1' />
                            {event.memberAmount}
                          </div>
                        </div>
                      </div>
                    )}
                    {/* Member ID Verification */}
                    {event.eventType === 'paid' &&
                      formData.registrationType === 'member' && (
                        <div className='p-4 bg-gradient-to-r from-purple-900/10 to-blue-900/10 rounded-xl border border-purple-700/20'>
                          <label className='block text-sm font-medium text-gray-300 mb-3'>
                            Member ID Verification *
                          </label>
                          <div className='flex gap-3'>
                            <div className='flex-1'>
                              <div className='relative'>
                                <FaIdCard className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400' />
                                <input
                                  type='text'
                                  name='memberId'
                                  value={formData.memberId}
                                  onChange={handleInputChange}
                                  onBlur={handleMemberIdBlur}
                                  required
                                  className='w-full pl-10 pr-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent'
                                  placeholder='Enter your Member ID'
                                />
                              </div>
                            </div>
                            <button
                              type='button'
                              onClick={verifyMemberId}
                              disabled={
                                verifyingMember || !formData.memberId.trim()
                              }
                              className='px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:from-gray-700 disabled:to-gray-800 disabled:cursor-not-allowed rounded-lg font-medium flex items-center gap-2 min-w-[120px] justify-center'
                            >
                              {verifyingMember ? (
                                <FaSpinner className='animate-spin' />
                              ) : (
                                <FaCheckCircle />
                              )}
                              {verifyingMember ? 'Verifying...' : 'Verify ID'}
                            </button>
                          </div>

                          {formData.memberVerified && (
                            <div className='mt-3 p-3 bg-gradient-to-r from-green-900/20 to-emerald-900/20 rounded-lg border border-green-700/50'>
                              <div className='flex items-center gap-2 text-green-400 mb-2'>
                                <FaCheckCircle />
                                <span className='font-bold'>
                                  Member Verified Successfully!
                                </span>
                              </div>
                              {formData.memberDetails && (
                                <div className='grid grid-cols-1 md:grid-cols-2 gap-2 mt-2 text-sm'>
                                  <div>
                                    <span className='text-gray-400'>
                                      Member ID:
                                    </span>
                                    <span className='text-white ml-2 font-mono'>
                                      {formData.memberDetails.memberId}
                                    </span>
                                  </div>
                                  <div>
                                    <span className='text-gray-400'>Name:</span>
                                    <span className='text-white ml-2'>
                                      {formData.memberDetails.name || 'N/A'}
                                    </span>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                    {/* Amount Summary */}
                    {event.eventType === 'paid' && (
                      <div className='p-4 bg-gradient-to-r from-purple-900/30 to-pink-900/30 rounded-xl border border-purple-700/50'>
                        <div className='flex flex-col md:flex-row justify-between items-center'>
                          <div>
                            <p className='text-gray-300 text-sm'>
                              Registration Type:
                            </p>
                            <p className='text-white font-bold'>
                              {isSamePrice
                                ? '📝 Event Registration'
                                : formData.registrationType === 'member'
                                ? '👤 Member Registration'
                                : '👥 Non-Member Registration'}
                            </p>
                          </div>
                          <div className='text-center md:text-right mt-4 md:mt-0'>
                            <p className='text-gray-300 text-sm'>
                              Amount to Pay:
                            </p>
                            <div className='flex items-center justify-center md:justify-end text-2xl md:text-3xl font-bold text-white'>
                              <FaRupeeSign className='mr-1' />
                              {calculateAmount()}
                              <span className='text-sm text-gray-400 ml-2'>
                                INR
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Personal Information */}
                    <div className='p-4 bg-gray-800/30 rounded-xl'>
                      <h3 className='text-lg font-bold text-white mb-4'>
                        Personal Information
                      </h3>
                      <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                        <div>
                          <label className='block text-sm font-medium text-gray-300 mb-2'>
                            Full Name *
                          </label>
                          <input
                            type='text'
                            name='name'
                            value={formData.name}
                            onChange={handleInputChange}
                            required
                            className='w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent'
                            placeholder='Enter your full name'
                          />
                        </div>

                        <div>
                          <label className='block text-sm font-medium text-gray-300 mb-2'>
                            Email Address *
                          </label>
                          <input
                            type='email'
                            name='email'
                            value={formData.email}
                            onChange={handleInputChange}
                            required
                            className='w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent'
                            placeholder='Enter your email'
                          />
                        </div>

                        <div>
                          <label className='block text-sm font-medium text-gray-300 mb-2'>
                            Phone Number *
                          </label>
                          <input
                            type='tel'
                            name='phone'
                            value={formData.phone}
                            onChange={handleInputChange}
                            required
                            className='w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent'
                            placeholder='Enter your phone number'
                          />
                        </div>

                        <div>
                          <label className='block text-sm font-medium text-gray-300 mb-2'>
                            City *
                          </label>
                          <input
                            type='text'
                            name='city'
                            value={formData.city}
                            onChange={handleInputChange}
                            required
                            className='w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent'
                            placeholder='Enter your city'
                          />
                        </div>

                        <div className='md:col-span-2'>
                          <label className='block text-sm font-medium text-gray-300 mb-2'>
                            State *
                          </label>
                          <input
                            type='text'
                            name='state'
                            value={formData.state}
                            onChange={handleInputChange}
                            required
                            className='w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent'
                            placeholder='Enter your state'
                          />
                        </div>
                      </div>
                    </div>

                    {/* Professional Information */}
                    <div className='p-4 bg-gray-800/30 rounded-xl'>
                      <h3 className='text-lg font-bold text-white mb-4'>
                        Professional Information
                      </h3>

                      {/* ✅ CONDITIONAL: You Want to Join As - Only show if data exists */}
                      {joinAsOptions.length > 0 && (
                        <div className='mb-6'>
                          <label className='block text-sm font-medium text-gray-300 mb-3'>
                            You Want to Join As
                          </label>
                          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3'>
                            {joinAsOptions.map(option => (
                              <label
                                key={option}
                                className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer border ${
                                  formData.joinAs === option.toLowerCase()
                                    ? 'bg-teal-900/40 border-teal-500'
                                    : 'bg-gray-800/40 border-gray-700'
                                }`}
                              >
                                <input
                                  type='radio'
                                  name='joinAs'
                                  value={option.toLowerCase()}
                                  checked={
                                    formData.joinAs === option.toLowerCase()
                                  }
                                  onChange={handleInputChange}
                                  className='w-4 h-4 text-teal-500'
                                />
                                <span className='text-gray-300'>{option}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Company Name */}
                      <div className='mb-6'>
                        <label className='block text-sm font-medium text-gray-300 mb-2'>
                          Organisation/Company Name
                        </label>
                        <input
                          type='text'
                          name='companyName'
                          value={formData.companyName}
                          onChange={handleInputChange}
                          className='w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent'
                          placeholder='Enter your organisation or company name'
                        />
                      </div>

                      {/* ✅ CONDITIONAL: Business Challenges - Only show if data exists */}
                      {challengeOptions.length > 0 && (
                        <div className='mb-6'>
                          <label className='block text-sm font-medium text-gray-300 mb-3'>
                            What is the biggest challenge your business faces
                            today? (Select all that apply)
                          </label>
                          <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
                            {challengeOptions.map(challenge => (
                              <label
                                key={challenge}
                                className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer border ${
                                  formData.challenge.includes(challenge)
                                    ? 'bg-teal-900/40 border-teal-500'
                                    : 'bg-gray-800/40 border-gray-700'
                                }`}
                              >
                                <input
                                  type='checkbox'
                                  name='challenge'
                                  value={challenge}
                                  checked={formData.challenge.includes(
                                    challenge
                                  )}
                                  onChange={handleInputChange}
                                  className='w-4 h-4 text-teal-500 rounded'
                                />
                                <span className='text-gray-300'>
                                  {challenge}
                                </span>
                              </label>
                            ))}
                          </div>
                          {formData.challenge.length > 0 && (
                            <div className='mt-3 p-2 bg-teal-900/20 rounded-lg border border-teal-700/30'>
                              <p className='text-teal-400 text-sm'>
                                ✅ Selected:{' '}
                                {Array.isArray(formData.challenge)
                                  ? formData.challenge.join(', ')
                                  : formData.challenge}
                              </p>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Business Description */}
                      <div className='mb-6'>
                        <label className='block text-sm font-medium text-gray-300 mb-2'>
                          About your Business (Only for MSME & Startups)
                        </label>
                        <textarea
                          name='businessDescription'
                          value={formData.businessDescription}
                          onChange={handleInputChange}
                          rows='4'
                          className='w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none'
                          placeholder='Tell us about your business'
                        />
                      </div>

                      {/* Other Information */}
                      {event?.showOtherInfo && (
                        <div>
                          <label className='block text-sm font-medium text-gray-300 mb-2'>
                            Other Information (Optional)
                          </label>
                          <textarea
                            name='otherInformation'
                            value={formData.otherInformation}
                            onChange={handleInputChange}
                            rows='3'
                            className='w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none'
                            placeholder='Any additional information...'
                          />
                        </div>
                      )}
                    </div>
                    {/* Photo Upload (Optional) */}
                    {event?.showPhotoUpload && (
                      <div className='p-4 bg-gray-800/30 rounded-xl'>
                        <label className='block text-sm font-medium text-gray-300 mb-2'>
                          Upload Photo (Optional)
                        </label>

                        <label className='flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-600 rounded-lg cursor-pointer bg-gray-800/30 hover:bg-gray-800/50'>
                          <div className='flex flex-col items-center justify-center pt-5 pb-6'>
                            <p className='text-sm text-gray-400'>
                              Click to upload photo
                            </p>
                            <p className='text-xs text-gray-500'>
                              JPG, PNG up to 5MB
                            </p>
                          </div>

                          <input
                            type='file'
                            name='photo'
                            accept='image/*'
                            onChange={handleInputChange}
                            className='hidden'
                          />
                        </label>

                        {formData.photo && (
                          <div className='mt-2 p-2 bg-teal-900/20 rounded-lg border border-teal-700/30'>
                            <p className='text-teal-400 text-sm'>
                              ✅ Selected: {formData.photo.name}
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* File Upload */}
                    {event?.showDocumentUpload && (
                      <div className='p-4 bg-gray-800/30 rounded-xl'>
                        <label className='block text-sm font-medium text-gray-300 mb-2'>
                          Upload Required Documents(Optional)
                        </label>
                        <div className='mt-2'>
                          <label className='flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-600 rounded-lg cursor-pointer bg-gray-800/30 hover:bg-gray-800/50'>
                            <div className='flex flex-col items-center justify-center pt-5 pb-6'>
                              <svg
                                className='w-8 h-8 mb-4 text-gray-400'
                                fill='none'
                                stroke='currentColor'
                                viewBox='0 0 24 24'
                              >
                                <path
                                  strokeLinecap='round'
                                  strokeLinejoin='round'
                                  strokeWidth='2'
                                  d='M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12'
                                ></path>
                              </svg>
                              <p className='mb-2 text-sm text-gray-400'>
                                <span className='font-semibold'>
                                  Click to upload
                                </span>
                              </p>
                              <p className='text-xs text-gray-500'>
                                PDF, DOC, PPT up to 10MB
                              </p>
                            </div>
                            <input
                              type='file'
                              name='file'
                              onChange={handleInputChange}
                              className='hidden'
                              accept='.pdf,.doc,.docx,.ppt,.pptx'
                            />
                          </label>
                          {formData.file && (
                            <div className='mt-2 p-2 bg-teal-900/20 rounded-lg border border-teal-700/30'>
                              <p className='text-teal-400 text-sm'>
                                ✅ Selected: {formData.file.name}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* ✅ CAPTCHA - Added back */}
                    <div className='flex justify-center pt-4'>
                      <ReCAPTCHA
                        sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                        onChange={handleCaptchaChange}
                        theme='dark'
                      />
                    </div>

                    {/* Submit Button */}
                    <div className='pt-4'>
                      <button
                        type='submit'
                        disabled={submitting}
                        className={`w-full py-4 px-6 rounded-xl font-bold text-lg ${
                          submitting
                            ? 'bg-gray-700 cursor-not-allowed text-gray-400'
                            : event.eventType === 'paid'
                            ? 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white'
                            : 'bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700 text-white'
                        }`}
                      >
                        {submitting ? (
                          <span className='flex items-center justify-center'>
                            <FaSpinner className='animate-spin mr-3' />
                            Processing...
                          </span>
                        ) : event.eventType === 'paid' ? (
                          <span className='flex items-center justify-center gap-2'>
                            <FaCreditCard />
                            Proceed to Payment - ₹{calculateAmount()}
                          </span>
                        ) : (
                          <span className='flex items-center justify-center gap-2'>
                            <FaCheckCircle />
                            Register Now
                          </span>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* Step 2: Payment */}
              {registrationStep === 2 && (
                <div className='bg-gradient-to-r from-gray-800/50 to-gray-900/50 rounded-2xl p-6 md:p-8 border border-gray-700 backdrop-blur-sm'>
                  <div className='flex items-center justify-between mb-6 pb-4 border-b border-gray-700'>
                    <h2 className='text-2xl font-bold text-white'>
                      Complete Payment
                    </h2>
                    <div className='text-sm text-gray-400'>Step 2 of 3</div>
                  </div>

                  <div className='text-center py-8'>
                    <div className='inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-full mb-6'>
                      <FaCreditCard className='w-10 h-10 text-purple-400' />
                    </div>
                    <h3 className='text-2xl font-bold text-white mb-4'>
                      Payment Summary
                    </h3>

                    <div className='max-w-md mx-auto bg-gray-800/30 rounded-xl p-6 mb-6 border border-gray-700'>
                      <div className='space-y-4'>
                        <div className='flex justify-between items-center'>
                          <span className='text-gray-300'>Event:</span>
                          <span className='text-white font-medium'>
                            {event.title}
                          </span>
                        </div>
                        <div className='flex justify-between items-center'>
                          <span className='text-gray-300'>
                            Registration Type:
                          </span>
                          <span className='text-white font-medium'>
                            {formData.registrationType === 'member'
                              ? 'Member'
                              : 'Non-Member'}
                          </span>
                        </div>
                        <div className='border-t border-gray-700 pt-4'>
                          <div className='flex justify-between items-center'>
                            <span className='text-gray-300 text-lg'>
                              Total Amount:
                            </span>
                            <div className='flex items-center text-2xl font-bold text-white'>
                              <FaRupeeSign className='mr-1' />
                              {calculateAmount()}
                              <span className='text-sm text-gray-400 ml-2'>
                                INR
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className='max-w-md mx-auto bg-gradient-to-r from-blue-900/10 to-purple-900/10 rounded-xl p-6 mb-6 border border-blue-700/30'>
                      <div className='flex items-center gap-3 mb-3'>
                        <FaShieldAlt className='text-blue-400' />
                        <h4 className='text-lg font-bold text-white'>
                          Secure Payment
                        </h4>
                      </div>
                      <p className='text-gray-300 text-sm mb-3'>
                        Your payment is secured with 256-bit SSL encryption.
                      </p>
                    </div>

                    <div className='flex flex-col md:flex-row gap-4 justify-center items-center'>
                      <button
                        onClick={handleGoBack}
                        className='px-8 py-3 bg-gray-700 hover:bg-gray-600 rounded-lg font-medium flex items-center gap-2 min-w-[180px] justify-center'
                      >
                        <FaArrowLeft />
                        Go Back & Edit
                      </button>
                      <button
                        onClick={handlePayment}
                        disabled={paymentLoading}
                        className={`px-8 py-3 rounded-lg font-bold flex items-center gap-2 min-w-[180px] justify-center ${
                          paymentLoading
                            ? 'bg-purple-700 cursor-not-allowed'
                            : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700'
                        }`}
                      >
                        {paymentLoading ? (
                          <>
                            <FaSpinner className='animate-spin' />
                            Processing...
                          </>
                        ) : (
                          <>
                            <FaCreditCard />
                            Pay ₹{calculateAmount()}
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Success */}
              {registrationStep === 3 && (
                <div className='bg-gradient-to-r from-gray-800/50 to-gray-900/50 rounded-2xl p-6 md:p-8 border border-gray-700 backdrop-blur-sm'>
                  <div className='text-center py-8'>
                    <div className='inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-full mb-6'>
                      <svg
                        className='w-10 h-10 text-green-500'
                        fill='none'
                        stroke='currentColor'
                        viewBox='0 0 24 24'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth='2'
                          d='M5 13l4 4L19 7'
                        ></path>
                      </svg>
                    </div>
                    <h3 className='text-2xl font-bold text-white mb-4'>
                      Registration Successful!
                    </h3>

                    {event.eventType === 'paid' && (
                      <div className='max-w-md mx-auto bg-gradient-to-r from-green-900/20 to-emerald-900/20 rounded-xl p-6 mb-6 border border-green-700/30'>
                        <div className='space-y-3'>
                          <div className='flex justify-between items-center'>
                            <span className='text-gray-300'>Amount Paid:</span>
                            <div className='flex items-center text-xl font-bold text-white'>
                              <FaRupeeSign className='mr-1' />
                              {calculateAmount()}
                            </div>
                          </div>
                          <div className='flex justify-between items-center'>
                            <span className='text-gray-300'>
                              Payment Status:
                            </span>
                            <span className='text-green-400 font-bold'>
                              Completed
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className='max-w-md mx-auto bg-gray-800/30 rounded-xl p-6 mb-6'>
                      <h4 className='text-lg font-bold text-white mb-3'>
                        What's Next?
                      </h4>
                      <ul className='text-left space-y-2'>
                        <li className='flex items-start gap-2'>
                          <span className='text-green-400'>✓</span>
                          <span className='text-gray-300'>
                            Confirmation email sent to{' '}
                            <strong>{formData.email}</strong>
                          </span>
                        </li>
                        <li className='flex items-start gap-2'>
                          <span className='text-green-400'>✓</span>
                          <span className='text-gray-300'>
                            Event details will be shared soon
                          </span>
                        </li>
                      </ul>
                    </div>

                    <p className='text-gray-300 mb-8 max-w-md mx-auto'>
                      Thank you for registering for{' '}
                      <strong>{event.title}</strong>.
                    </p>

                    <div className='flex flex-col sm:flex-row gap-4 justify-center'>
                      <button
                        onClick={handleBack}
                        className='px-8 py-3 bg-teal-600 hover:bg-teal-700 rounded-lg font-medium flex items-center gap-2 justify-center'
                      >
                        <FaCalendarAlt />
                        Back to Events
                      </button>
                      <button
                        onClick={() => navigate('/')}
                        className='px-8 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 rounded-lg font-medium flex items-center gap-2 justify-center'
                      >
                        <FaBuilding />
                        Go to Home
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  )
}

export default EventFormPage
